/**
 * Bringing data in.
 *
 * Everything happens in this tab. The file is read here, sniffed here, parsed here
 * and its column types are decided here — so a pasted table is understood before a
 * round trip could have finished, a forty-megabyte workbook never leaves the
 * machine it is on, and the server never runs a spreadsheet parser over something
 * a stranger uploaded.
 *
 * Which makes this screen quick, and being quick is most of what it has to be. The
 * brief's test is "a non-technical user should be able to drag in a file and
 * BroadPaper should do as much work as possible automatically" — so there is no
 * step between the drop and the review. No format question, no delimiter question,
 * no "does your file have headers" question, and no wizard: the file lands, and the
 * next thing on screen is what BroadPaper understood.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { IngestError, type Dataset, type IngestResult } from "@broadpaper/ingest";
import { api, helpUrl } from "../api.js";
import { readOffThread } from "../ingest/index.js";
import { AnotherClick, CONNECTOR_HELP, Cancelled, ConnectorError, PROVIDERS, available, pickFile, prepare, type FileLink, type Provider } from "../connectors/index.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Button, Card, Field, Icon, Note, bytes, count } from "../ui/index.js";
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";

/** What the file input offers. The sniffer does not trust any of it. */
const ACCEPT = ".csv,.tsv,.txt,.xlsx,.json,.ndjson,.jsonl,text/csv,text/tab-separated-values,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Help for each thing that can go wrong here, one section each.
 *
 * Written out in full rather than built from a section name, so that
 * `tests/site/support.test.ts` can find every one in this file and fail if the
 * section it points at is renamed or removed. This screen is where most people who
 * get stuck get stuck, and a help link that lands at the top of the wrong page is
 * worse than none.
 */
const HELP = {
  formats: helpUrl("/support/importing-data/#formats"),
  tooLarge: helpUrl("/support/importing-data/#file-too-large"),
  tooManyRows: helpUrl("/support/importing-data/#too-many-rows"),
  oldExcel: helpUrl("/support/importing-data/#old-excel"),
  unreadable: helpUrl("/support/importing-data/#unreadable-workbook"),
  empty: helpUrl("/support/importing-data/#empty-file"),
  headings: helpUrl("/support/importing-data/#headings"),
  dates: helpUrl("/support/importing-data/#dates"),
  columnTypes: helpUrl("/support/importing-data/#column-types"),
  blank: helpUrl("/support/importing-data/#blank-columns"),
  json: helpUrl("/support/importing-data/#json")
} as const;

/** Something that stopped a file being read, said where it stays on screen. */
interface Problem {
  message: string;
  hint?: string | undefined;
  help: string;
}

/**
 * Which help section a reader's message belongs to.
 *
 * Matched on the words of the message and its hint, which the help page quotes
 * verbatim and a test holds it to — so the two cannot drift apart without a test
 * failing.
 */
function helpFor(message: string, hint = ""): string {
  const text = `${message} ${hint}`;
  if (/as an Excel workbook|unusual number of parts|expands to far more|as a spreadsheet/i.test(text)) return HELP.unreadable;
  if (/older Excel workbook/i.test(text)) return HELP.oldExcel;
  if (/no data in it|nothing in this|is empty|headings but no rows/i.test(text)) return HELP.empty;
  if (/JSON|list of records/i.test(text)) return HELP.json;
  return HELP.formats;
}

type ReaderNotice = Dataset["notices"][number];

/**
 * A reader's notice, in words that promise nothing the screen does not do.
 *
 * The heading-row notice ends "say so and they will be used", which is true of the
 * reader and not of this screen: there is no switch for it here. So it is said
 * without the promise, with a link to what to do in the file instead.
 */
function noticeWords(notice: ReaderNotice): string {
  if (notice.action === "confirm-header-row") return "The first row looked like data rather than headings, so the columns have been numbered.";
  return notice.message;
}

function noticeHelp(notice: ReaderNotice): string | null {
  if (notice.action === "confirm-header-row") return HELP.headings;
  if (notice.action === "confirm-date-order") return HELP.dates;
  if (notice.action === "check-type") return HELP.columnTypes;
  if (/empty in/i.test(notice.message)) return HELP.blank;
  return null;
}

/** Whether the reader stopped at the row limit — the one notice that means rows are missing. */
function truncated(notice: ReaderNotice): boolean {
  return /larger than BroadPaper reads|have been brought in/i.test(notice.message);
}

type Stage =
  | { kind: "idle" }
  | { kind: "reading"; name: string }
  /** A vendor's sign-in or picker is open, and nothing has come back yet. */
  | { kind: "fetching"; provider: Provider }
  /** `link` when the file came from Google Drive or OneDrive, so it can be refreshed from there. */
  | { kind: "chose"; result: IngestResult; sheet: number; name: string; how: "file" | "paste"; link?: FileLink | undefined }
  | { kind: "saving" };

export function DataImport() {
  const { view, refresh } = useWorkspace();
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [over, setOver] = useState(false);
  const [pasted, setPasted] = useState("");
  /*
   * Why the last file did not come in, kept on screen.
   *
   * It was a toast, which is gone in nine seconds — about the time it takes to read
   * "save it again as .xlsx" and go to look for Excel's Save As. A reason somebody
   * has to act on stays until they try again, with a link to what to do.
   */
  const [problem, setProblem] = useState<Problem | null>(null);
  const paste = useRef<HTMLTextAreaElement>(null);

  const full = atLimit(view, "datasets");
  const upgrade = upgradeTarget(view);

  const read = useCallback(
    async (run: () => Promise<IngestResult> | IngestResult, name: string, how: "file" | "paste", link?: FileLink) => {
      setProblem(null);
      setStage({ kind: "reading", name });
      try {
        // Read in a worker (`readOffThread`), so the "reading" state paints and stays
        // responsive however long the file takes — fifty thousand rows used to freeze
        // the tab for seconds with nothing on screen to say why.
        const result = await run();
        setStage({ kind: "chose", result, sheet: 0, name, how, link });
      } catch (e) {
        setStage({ kind: "idle" });
        if (e instanceof IngestError) setProblem({ message: e.message, hint: e.hint, help: helpFor(e.message, e.hint) });
        else reportFailure(e, "We couldn't read that file.");
      }
    },
    []
  );

  const onFile = useCallback(
    (file: File) => {
      if (file.size > view.limits.uploadBytes) {
        setProblem({
          message: `That file is ${bytes(file.size)}, and your plan takes files up to ${bytes(view.limits.uploadBytes)}.`,
          hint: "Nothing has been read. A copy with just the sheet you need, a CSV, or pasting the rows usually gets under it.",
          help: HELP.tooLarge
        });
        return;
      }
      void read(() => readOffThread({ kind: "file", file, options: { maxRows: view.limits.rowsPerDataset, maxColumns: 200 } }), file.name, "file");
    },
    [read, view.limits.uploadBytes, view.limits.rowsPerDataset]
  );

  /** The service whose next step needs a click of its own. See `AnotherClick`. */
  const [continuing, setContinuing] = useState<Provider | null>(null);

  useEffect(() => {
    // The vendors' scripts, loaded as the screen opens: a sign-in window opened after
    // waiting for them is one the browser blocks.
    for (const provider of available()) prepare(provider);
  }, []);

  /*
   * A file from Google Drive or OneDrive.
   *
   * `pickFile` is the first thing the click does, with nothing awaited before it,
   * because it opens the vendor's sign-in window. What comes back is bytes, read here
   * exactly as a dropped file is — plus where they came from, so the data can be
   * refreshed from the same file next month.
   */
  const fromService = async (provider: Provider) => {
    setProblem(null);
    setContinuing(null);
    setStage({ kind: "fetching", provider });
    try {
      const fetched = await pickFile(provider, view.limits.uploadBytes);
      if (!fetched) return setStage({ kind: "idle" });
      const { bytes: data, filename, link } = fetched;
      await read(() => readOffThread({ kind: "bytes", bytes: data, options: { filename, maxRows: view.limits.rowsPerDataset, maxColumns: 200 } }), link.name, "file", link);
    } catch (e) {
      setStage({ kind: "idle" });
      if (e instanceof Cancelled) return;
      if (e instanceof AnotherClick) return setContinuing(provider);
      if (e instanceof ConnectorError) return setProblem({ message: e.message, hint: e.hint, help: CONNECTOR_HELP });
      reportFailure(e, `We couldn't open ${PROVIDERS[provider].name}.`);
    }
  };

  const save = async (stage: Extract<Stage, { kind: "chose" }>, dataset: Dataset) => {
    const { name } = stage;
    setStage({ kind: "saving" });
    try {
      const created = await api.createDataset({
        name,
        // What the parse decided this *is*, not how it arrived. A file dropped on
        // the page is a csv or a workbook by the time it reaches here, and sending
        // "file" was a value the service has no name for, so a perfectly good
        // upload failed validation after the user had already waited for the parse.
        source: stage.how === "paste" ? "paste" : stage.result.format,
        columns: dataset.columns,
        rows: dataset.rows,
        headerRow: dataset.headerRow,
        // Which sheet only when there was a choice: a refresh looks for it by name, and a
        // one-sheet workbook whose only tab is renamed should still refresh.
        ...(stage.link ? { link: { ...stage.link, ...(stage.result.datasets.length > 1 ? { sheet: dataset.label } : {}) } } : {})
      });
      await refresh();
      // Straight to the review screen. Saving and then asking the user to go and
      // look at it is a step nobody needs.
      go(`/data/${created.dataset.id}`);
    } catch (e) {
      // Back to exactly the screen they were on, with the parsed file intact. A
      // failed save must not cost somebody the upload they already waited for.
      setStage(stage);
      reportFailure(e, "We couldn't save that.");
    }
  };

  if (full) {
    return (
      <div className="wrap--narrow stack" style={{ gap: 18 }}>
        <div className="head__title">
          <span className="eyebrow">Your data</span>
          <h1>You have reached your plan's limit</h1>
        </div>
        <Note tone="ask" title={`Your plan holds ${view.limits.datasets} sets of data`}>
          Delete one you no longer need, or {upgrade ? `move to ${upgrade.name} and the limit goes up straight away.` : "get in touch and we will raise it."}
        </Note>
        <div className="row" style={{ gap: 8 }}>
          <a className="btn" href="/data">
            Back to your data
          </a>
          {upgrade ? (
            <a className="btn btn--primary" href="/account">
              See {upgrade.name}
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  /* ------------------------------- the sheet picker ------------------------ */

  if (stage.kind === "chose") {
    const { result } = stage;
    const dataset = result.datasets[stage.sheet]!;
    /*
     * What the reader noticed, before anything is saved.
     *
     * The reader has always said when it stopped at the row limit, when a column's
     * dates could be day first or month first, when it could not find the headings —
     * and nothing showed any of it, so a four-thousand-row file on a two-thousand-row
     * plan came in as two thousand rows without a word. These are the moments
     * somebody can still fix the file, which is after reading and before saving.
     */
    const notices = [...result.notices, ...dataset.notices];
    const stopped = notices.some(truncated);
    const nothing = dataset.columns.length === 0 || dataset.rows.length === 0;
    return (
      <div className="wrap--narrow stack" style={{ gap: 20 }}>
        <div className="head__title">
          <span className="eyebrow">Your data</span>
          <h1>{result.datasets.length > 1 ? "Which sheet?" : "Ready to bring in"}</h1>
          <p className="head__lede">
            {result.datasets.length > 1
              ? `This workbook has ${result.datasets.length} sheets with data in them. Choose the one this document should use — you can bring in another later.`
              : `${count(dataset.rows.length)} row${dataset.rows.length === 1 ? "" : "s"} and ${dataset.columns.length} column${dataset.columns.length === 1 ? "" : "s"}. The next screen shows what BroadPaper understood.`}
          </p>
          {stage.link ? (
            <p className="small muted row" style={{ gap: 6, margin: 0 }}>
              <Icon name="cloud" /> From {PROVIDERS[stage.link.provider].name}. Next month, update the file there and press Refresh on this data.
            </p>
          ) : null}
        </div>

        {nothing ? (
          <Note tone="bad" title={dataset.columns.length ? "There are headings here, but no rows underneath them" : "There's nothing in this to bring in"}>
            Check that you chose the right file{result.datasets.length > 1 ? " or sheet" : ""}, and that the rows are in it rather than in a linked workbook.{" "}
            <a href={HELP.empty} target="_blank" rel="noreferrer">
              What to check
            </a>
          </Note>
        ) : notices.length ? (
          <Note tone={stopped || notices.some((n) => n.level !== "info") ? "ask" : "info"} title="Worth knowing before you bring it in">
            <ul className="notice-list">
              {stopped ? (
                <li>
                  <strong>
                    Your plan holds {count(view.limits.rowsPerDataset)} rows in one set of data, so BroadPaper stopped reading there. Rows after that point are not
                    included.
                  </strong>{" "}
                  <a href={HELP.tooManyRows} target="_blank" rel="noreferrer">
                    What to do
                  </a>
                </li>
              ) : null}
              {notices
                .filter((n) => !truncated(n))
                .map((notice, i) => {
                  const help = noticeHelp(notice);
                  return (
                    <li key={i}>
                      {noticeWords(notice)}{" "}
                      {help ? (
                        <a href={help} target="_blank" rel="noreferrer">
                          More
                        </a>
                      ) : null}
                    </li>
                  );
                })}
            </ul>
          </Note>
        ) : null}

        {result.datasets.length > 1 ? (
          <div className="grid grid--2">
            {result.datasets.map((sheet, i) => (
              <button key={sheet.label} type="button" className={`tile${i === stage.sheet ? " tile--chosen" : ""}`} onClick={() => setStage({ ...stage, sheet: i })}>
                <div className="stack" style={{ gap: 6 }}>
                  <strong>{sheet.label}</strong>
                  <span className="small muted">
                    {count(sheet.rows.length)} rows · {sheet.columns.length} columns
                  </span>
                  <span className="small muted truncate">{sheet.columns.filter((c) => c.include).map((c) => c.label).slice(0, 4).join(", ")}</span>
                </div>
              </button>
            ))}
          </div>
        ) : null}

        <Card>
          <div className="card__body">
            <Field label="What should this be called?" id="dataset-name" hint="Just for you — it is how you will recognise it later.">
              <input id="dataset-name" className="input" value={stage.name} onChange={(e) => setStage({ ...stage, name: e.target.value })} maxLength={200} />
            </Field>
          </div>
          <div className="card__foot row" style={{ justifyContent: "space-between", gap: 12 }}>
            <Button variant="quiet" onClick={() => setStage({ kind: "idle" })}>
              Start again
            </Button>
            <Button variant="primary" icon="chevron" disabled={nothing} onClick={() => void save({ ...stage, name: stage.name.trim() || dataset.label }, dataset)}>
              Bring it in
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  /* ------------------------------- the drop zone --------------------------- */

  const busy = stage.kind === "reading" || stage.kind === "saving" || stage.kind === "fetching";
  const services = available();

  return (
    <div className="wrap--narrow stack" style={{ gap: 22 }}>
      <div className="head__title">
        <span className="eyebrow">Your data</span>
        <h1>Bring in your data</h1>
        <p className="head__lede">
          Drag in a spreadsheet, or copy rows out of Excel and paste them below. BroadPaper reads it straight away and tells you what it found — nothing
          is uploaded until you say so.
        </p>
      </div>

      {problem ? (
        <Note tone="bad" title={problem.message}>
          {problem.hint ? `${problem.hint} ` : ""}
          <a href={problem.help} target="_blank" rel="noreferrer">
            What to do about this
          </a>
        </Note>
      ) : null}

      <label
        className={`drop${over ? " drop--over" : ""}${busy ? " drop--busy" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files[0];
          if (file && !busy) onFile(file);
        }}
      >
        {stage.kind === "fetching" ? (
          <>
            <span className="spinner spinner--lg" />
            <strong>Waiting for {PROVIDERS[stage.provider].name}…</strong>
            <span className="small muted">Sign in and choose the file in the window {PROVIDERS[stage.provider].name} opened.</span>
          </>
        ) : busy ? (
          <>
            <span className="spinner spinner--lg" />
            <strong>{stage.kind === "saving" ? "Bringing it in…" : `Reading ${stage.kind === "reading" ? stage.name : "your file"}…`}</strong>
            <span className="small muted">This happens on your own machine.</span>
          </>
        ) : (
          <>
            <Icon name="upload" className="drop__icon" />
            <strong style={{ fontSize: 17 }}>{over ? "Drop it here" : "Drop a file here, or choose one"}</strong>
            <span className="small muted">Excel (.xlsx), CSV, tab-separated or JSON · up to {bytes(view.limits.uploadBytes)}</span>
            <input type="file" accept={ACCEPT} onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = "";
            }} />
          </>
        )}
      </label>

      {/* Only the services this build has an app registered with; see `PROVIDERS`. */}
      {services.length ? (
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span className="small muted">Or choose it from</span>
          {services.map((provider) => (
            <Button key={provider} icon="cloud" disabled={busy} onClick={() => void fromService(provider)}>
              {PROVIDERS[provider].name}
            </Button>
          ))}
        </div>
      ) : null}

      {continuing ? (
        <Note tone="ask" title={`${PROVIDERS[continuing].name} needs you to confirm one more step`}>
          A browser lets each click open one window, and signing in the first time can need two.{" "}
          <Button size="sm" variant="primary" onClick={() => void fromService(continuing)}>
            Continue
          </Button>
        </Note>
      ) : null}

      <div className="stack" style={{ gap: 10 }}>
        <Field
          label="Or paste your rows"
          id="paste"
          hint="Select the cells in your spreadsheet, copy, and paste here. Include the heading row if you have one."
        >
          <textarea
            id="paste"
            ref={paste}
            className="input"
            value={pasted}
            placeholder={"Client\tPortfolio Value\tAs At\nAldermead Ltd\t£482,310.40\t31/08/2026"}
            onChange={(e) => setPasted(e.target.value)}
            disabled={busy}
          />
        </Field>
        <div className="row" style={{ gap: 8 }}>
          <Button
            variant="primary"
            disabled={busy || pasted.trim().length === 0}
            onClick={() => void read(() => readOffThread({ kind: "text", text: pasted, options: { maxRows: view.limits.rowsPerDataset, maxColumns: 200 } }), "Pasted data", "paste")}
          >
            Read what I pasted
          </Button>
          {pasted ? (
            <Button variant="quiet" onClick={() => setPasted("")}>
              Clear
            </Button>
          ) : null}
        </div>
      </div>

      {/* For the document somebody has the details of in their head rather than in a
          file — telling them to make a spreadsheet first so it can be read back is a
          worse job than typing it here. */}
      <Card flat>
        <div className="card__body spread" style={{ flexWrap: "wrap", gap: 14 }}>
          <div className="stack" style={{ gap: 4 }}>
            <strong>Or type it in</strong>
            <span className="small muted">No spreadsheet to hand? Start with an empty table — name the columns, choose what each holds, and type the rows.</span>
          </div>
          <a className="btn" href="/data/new/table">
            <Icon name="pencil" /> Type it in
          </a>
        </div>
      </Card>

      <Note tone="info" title="What BroadPaper does with it">
        It works out which columns you have, what kind of value each one holds — money, a date, a percentage — and how each should look in a document.
        You get to check all of that on the next screen before anything is used.
      </Note>

      <p className="small muted">
        A file not coming in the way you expected?{" "}
        <a href={HELP.formats} target="_blank" rel="noreferrer">
          Help with bringing in data
        </a>{" "}
        covers every message this screen can show, and what to do about each.
      </p>
    </div>
  );
}
